/**
 * Safe-Mode presentation — the header badge and the shell-level banner.
 * State comes from ./useSafeMode (one query, one status vocabulary).
 * Only `inactive` may ever read "Operating normally".
 */
import { ShieldAlert } from 'lucide-react';
import { Link } from 'react-router';
import { Badge, Icon, Text } from '@smms/design-system';
import { useSafeMode, type SafeModeStatus } from './useSafeMode';

/** Header badge. Only `inactive` may read "Operating normally". */
export function SafeModeBadge({ status }: { status: SafeModeStatus }) {
  switch (status) {
    case 'active':
      return (
        <Badge variant="danger" dot>
          Safe Mode active
        </Badge>
      );
    case 'inactive':
      return (
        <Badge variant="success" dot>
          Operating normally
        </Badge>
      );
    case 'loading':
      return (
        <Badge variant="neutral" dot>
          Checking Safe Mode…
        </Badge>
      );
    case 'not_entitled':
      return null;
    default:
      return (
        <Badge variant="warning" dot>
          Safe Mode state unknown
        </Badge>
      );
  }
}

/**
 * Shell-level banner: rendered by App above every route so no page can show
 * agent data without the brake state being visible. Silent unless the
 * server says Safe Mode is ACTIVE (unknown/not-entitled render nothing here;
 * the pages' own badges carry the unknown state). Needs a router (Link).
 */
export function SafeModeBanner() {
  const { status, state } = useSafeMode();
  if (status !== 'active' || !state) return null;
  return (
    <div
      role="status"
      data-testid="safe-mode-banner"
      className="flex flex-wrap items-center gap-3 border-b border-danger/40 bg-danger/10 px-6 py-2"
    >
      <Icon as={ShieldAlert} size="sm" className="text-danger" />
      <Text variant="body-sm" tone="danger" className="min-w-0 flex-1">
        Safe Mode is active — agent actions are halted
        {state.reason ? ` (${state.reason})` : ''}. Exiting requires a second approver.
      </Text>
      <Link to="/policies" className="text-sm font-medium text-danger underline">
        Policies &amp; Safety
      </Link>
    </div>
  );
}
